import {
  BottomBar,
  FormGroup,
  Header,
  Input,
  MainArea,
  Page,
  SaveBar,
  Select,
  SelectMulti,
  Alert,
} from '@salecycle/ui-component-library';
import { entityToTranslatedMultiSelectOptions } from 'components/utils/getSelectOptions';
import * as RestApi from 'core/network/RestApi';
import { RestApiError } from 'core/network/RestApi';
import useNotifications from 'core/notifications/useNotifications';
import AppTopBar from 'core/ui/components/AppTopBar/AppTopBar';
import Spinner from 'core/ui/components/spinner';
import { IApiEntity } from 'entities/entity';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { generatePath } from 'react-router';
import { useHistory, useParams } from 'react-router-dom';
import { useFeatureFlags } from 'core/app/AppContext';
import { LD_EMAIL_TRACKING_CONSENT_FLAG } from 'core/constants/EmailTrackingUnsubscribeSettings';
import { getSupportedLanguagesInAlphabetOrder } from 'sections/client-management/ui/pages/client-settings/supported-languages/constants/SupportedLanguagesConstants';
import { getDefaultTemplateBody, getCreateTemplateHeadingKey, getTemplateTypeOptions } from '../../constants';
import { TemplateApiEndpoints } from '../../constants/api-endpoints';
import { TemplateType } from '../../entities/template-type';
import { SaveTemplatePayload } from '../../entities/templates';
import { TemplateManagementRoute } from '../../routes';
import { useTemplateCreateDispatch, useTemplateCreateState } from './context/UseTemplateCreateContext';
import { useGetTemplateTypes } from './utils/useGetTemplateTypes';
import { useSite } from 'sections/hooks/useSite';

export const TemplateCreate: React.FC = () => {
  const { t } = useTranslation(['common', 'designs', 'supportedLanguages']);
  const dispatch = useTemplateCreateDispatch();
  const [templateName, setTemplateName] = React.useState<string>('');
  const [channel, setChannel] = React.useState<string>('');
  const [templateType, setTemplateType] = React.useState<string>('');
  const [languages, setLanguages] = React.useState<string[]>([]);
  const { push } = useHistory();
  const { showNotification } = useNotifications();
  const { siteId, channel: selectedChannel } = useParams<{ siteId: string; channel: string }>();
  const { data: site } = useSite(siteId);
  const { types } = useTemplateCreateState();
  const templateTypesQuery = useGetTemplateTypes();
  const { flagEnabled } = useFeatureFlags();
  const emailTrackingFeatureEnabled = flagEnabled(LD_EMAIL_TRACKING_CONSENT_FLAG);

  const templateTypeOptions = React.useMemo(() => getTemplateTypeOptions(types, channel, t, flagEnabled), [
    types,
    channel,
    t,
    flagEnabled,
  ]);

  React.useEffect(() => {
    if (selectedChannel === 'rcs' && !flagEnabled('ld-rcs')) {
      push(generatePath(TemplateManagementRoute.HOME, { siteId, channel: 'email' }));
      return;
    }
    const channel = selectedChannel || 'email';
    setChannel(channel);
  }, [selectedChannel, flagEnabled, push, siteId]);

  // Initial templateTypes load
  React.useEffect(() => {
    if (!templateTypesQuery.isLoading) {
      const loadedTypes: TemplateType = templateTypesQuery?.data ?? null;
      dispatch({
        type: 'SET_TEMPLATE_TYPES',
        payload: { types: loadedTypes },
      });
    }
  }, [templateTypesQuery.isLoading, dispatch, templateTypesQuery.data]);

  const handleReset = () => {
    setTemplateName('');
    setChannel('');
    setTemplateType('');
    setLanguages([]);
  };

  const handleExit = () => {
    push(generatePath(TemplateManagementRoute.HOME, { siteId, channel }));
  };

  const isFormValid = () => {
    if (templateName.length > 0 && channel !== '' && templateType !== '' && languages.length > 0) {
      return true;
    }
    return false;
  };

  const saveTemplate = useMutation((template: SaveTemplatePayload) =>
    RestApi.post<{ id: string }, SaveTemplatePayload>(
      generatePath(TemplateApiEndpoints.SAVE_TEMPLATE, { siteId }),
      template,
    ),
  );

  const handleSubmit = () => {
    const newTemplate = {
      type: templateType,
      channel,
      template: getDefaultTemplateBody(channel, templateType),
      templateName,
      enrichmentConfigIds: [],
      languages,
    } as SaveTemplatePayload;

    saveTemplate.mutate(newTemplate, {
      onSuccess: data => {
        showNotification(t('designs:designGallery.form.comment.notifications.createTemplateSuccess'), 'normal');
        push(generatePath(TemplateManagementRoute.HOME, { siteId, channel }));
      },
      onError: (error: RestApiError, variables, context) => {
        showNotification(t('designs:designGallery.form.errorHeading'), 'error');
      },
    });
  };

  const supportedLanguagesOptions = entityToTranslatedMultiSelectOptions<IApiEntity>(
    getSupportedLanguagesInAlphabetOrder(t),
    t,
  );
  const getSiteLanguages = () => {
    return supportedLanguagesOptions.filter(option => {
      for (const supportedLanguage of site?.supportedLanguagesSettings?.supportedLanguages ?? []) {
        if (option.value === supportedLanguage) {
          return true;
        }
      }
      return false;
    });
  };

  if (templateTypesQuery.isLoading) {
    <Spinner />;
  }
  return (
    <>
      <MainArea>
        <AppTopBar />
        <Page>
          <Header heading={t(getCreateTemplateHeadingKey(channel))} />
          <Input
            label={t('designs:designGallery.form.templateName')}
            ariaLabel={t('designs:designGallery.form.templateName')}
            placeholder={t('designs:designGallery.form.templateNamePlaceholder')}
            value={templateName}
            onChange={e => setTemplateName(e)}
            validationMessage={
              templateName.length > 0 ? null : t('designs:designGallery.form.validation.minCharacters')
            }
            validationState={templateName.length > 0 ? 'complete' : 'warning'}
          />

          <FormGroup childrenSpanWidth={true}>
            <div data-testid={'templateTypeSelect'}>
              <Select
                ariaLabel={t('designs:designGallery.form.templateType.label')}
                label={t('designs:designGallery.form.templateType.label')}
                hint={t('designs:designGallery.form.templateType.labelHint')}
                options={templateTypeOptions}
                placeholder={t('designs:designGallery.form.templateType.templateTypePlaceholder')}
                value={
                  templateType
                    ? { value: templateType, label: t('designs:designGallery.form.templateType.' + templateType) }
                    : null
                }
                onChange={e => setTemplateType(e.value)}
                clearable={true}
                validationMessage={templateType === '' ? t('designs:designGallery.form.validation.required') : null}
                validationState={templateType === '' ? 'warning' : 'complete'}
              />
            </div>
            <div data-testid={'languagesSelect'}>
              <SelectMulti
                ariaLabel={t('designs:designGallery.form.languages.label')}
                label={t('designs:designGallery.form.languages.label')}
                hint={t('designs:designGallery.form.languages.labelHint')}
                options={getSiteLanguages()}
                placeholder={t('designs:designGallery.form.languages.languagesPlaceholder')}
                value={supportedLanguagesOptions.filter(sl => languages.includes(sl.value))}
                onChange={e =>
                  e && e.length && e.length > 0 ? setLanguages(e.map(option => option.value)) : setLanguages([])
                }
                clearable={true}
                validationMessage={
                  languages === undefined || languages.length === 0
                    ? t('designs:designGallery.form.validation.required')
                    : null
                }
                validationState={languages === undefined || languages.length === 0 ? 'warning' : 'complete'}
              />
            </div>
            {templateType.length > 1 && templateType === 'globalUnsubscribe' && (
              <div>
                <Alert heading={t('designs:designGallery.globalUnsubscribeHeader')} variation="warning">
                  <p>{t('designs:designGallery.globalUnsubscribeWarning.globalUnsubscribeCreateWarning')}</p>
                </Alert>
              </div>
            )}
            {emailTrackingFeatureEnabled &&
              templateType.length > 1 &&
              templateType === 'globalUnsubscribeEmailTracking' && (
                <div>
                  <Alert heading={t('designs:designGallery.globalEmailTrackingUnsubscribeHeader')} variation="warning">
                    <p>
                      {t(
                        'designs:designGallery.globalEmailTrackingUnsubscribeWarning.globalEmailTrackingUnsubscribeCreateWarning',
                      )}
                    </p>
                  </Alert>
                </div>
              )}
          </FormGroup>
        </Page>
        <BottomBar>
          <SaveBar
            enableSaveButton={isFormValid()}
            onSaveButtonClicked={handleSubmit}
            onDiscardButtonClicked={handleReset}
            hasValidationFailed={!isFormValid()}
            onExitButtonClicked={handleExit}
            exitButtonLabel={t('designs:designGallery.form.exit')}
          />
        </BottomBar>
      </MainArea>
    </>
  );
};

export default TemplateCreate;
